"use client";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import useBrowseJobsFiltersStore from "@store/browseJobsFiltersStore";
import { useIntl } from "react-intl";
import useJobLoadingStore from "@store/isLoadingJobs";

const SalaryFilter = () => {
  const { setIsLoading }: any = useJobLoadingStore();

  const intl = useIntl();
  const translation = intl.messages;
  const jobsTranslation: any = translation["jobs"];
  const FiltersTranslation: any = jobsTranslation["filters"];

  const { range, setRange, resetRange } = useBrowseJobsFiltersStore();

  const [isOpen, setIsOpen] = useState(true);

  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState("0px");

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? `${contentRef.current.scrollHeight}px` : "0px");
    }
  }, [isOpen]);

  const sliderRef = useRef<HTMLDivElement>(null);
  const isDraggingFrom = useRef(false);
  const isDraggingTo = useRef(false);

  const minSalary = 0;
  const maxSalary = 130000;

  const toggleFilterVisibility = () => setIsOpen(!isOpen);

  const getSliderPosition = (clientX: number) => {
    if (sliderRef.current) {
      const rect = sliderRef.current.getBoundingClientRect();
      const percent = (clientX - rect.left) / rect.width;
      return percent * (maxSalary - minSalary) + minSalary;
    }
    return minSalary;
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDraggingFrom.current) {
      const newFrom = Math.min(getSliderPosition(e.clientX), range.to);
      setRange(newFrom, range.to);
    }
    if (isDraggingTo.current) {
      const newTo = Math.max(getSliderPosition(e.clientX), range.from);
      setRange(range.from, newTo);
    }
  };

  const handleMouseUp = () => {
    setIsLoading(true);

    isDraggingFrom.current = false;
    isDraggingTo.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMouseDownFrom = () => {
    isDraggingFrom.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseDownTo = () => {
    isDraggingTo.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div className="pt-5 mt-5">
      <button
        className="flex justify-between items-center w-full py-1"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-sm lg:text-[15px] font-medium text-[#1F2223]">
          {FiltersTranslation["salary-range"]}
        </span>

        <ChevronDownIcon
          className={`w-5 h-5 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        ref={contentRef}
        style={{ maxHeight: height }}
        className={`flex flex-col w-full overflow-hidden transition-max-height duration-500 ease-in-out`}
      >
        <div className="px-4 mt-5">
          <div className="flex flex-row items-center justify-between w-full">
            <span className="font-semibold text-[12px]">
              {FiltersTranslation["egp"]}{" "}
              {Math.floor(range.from).toLocaleString()}
            </span>
            <span className="font-semibold text-[12px]">
              {FiltersTranslation["egp"]}{" "}
              {Math.floor(range.to).toLocaleString()}
            </span>
          </div>

          <div className="new-range relative h-[12px]" ref={sliderRef}>
            <div className="w-full h-[4px] bg-[#F2F2F2] absolute top-[50%] transform -translate-y-1/2"></div>
            <div
              className="absolute h-[4px] bg-[#88D4E6] z-[1] top-[50%] transform -translate-y-1/2"
              style={{
                left: `${
                  ((range.from - minSalary) / (maxSalary - minSalary)) * 100
                }%`,
                right: `${
                  100 - ((range.to - minSalary) / (maxSalary - minSalary)) * 100
                }%`,
              }}
            ></div>

            <span
              className="border border-[#88D4E6] z-[3] bg-[#fff] rounded-full w-[12px] h-[12px] absolute top-[50%] transform -translate-y-1/2 cursor-pointer"
              style={{
                left: `${
                  ((range.from - minSalary) / (maxSalary - minSalary)) * 100
                }%`,
              }}
              onMouseDown={handleMouseDownFrom}
            ></span>
            <span
              className="border border-[#88D4E6] z-[3] bg-[#fff] rounded-full w-[12px] h-[12px] absolute top-[50%] transform -translate-y-1/2 cursor-pointer"
              style={{
                left: `${
                  ((range.to - minSalary) / (maxSalary - minSalary)) * 100
                }%`,
              }}
              onMouseDown={handleMouseDownTo}
            ></span>
          </div>

          <div className="flex flex-row items-center justify-between gap-[20px]">
            <input
              required
              type="number"
              value={Math.floor(range.from)}
              onChange={(e) => setRange(Number(e.target.value), range.to)}
              placeholder="From"
              className="focus:ring-transparent mt-[10px] rounded-[4px] !outline-none p-[5px] w-full h-[34px] border border-[#B9BDC2] text-[12px] font-normal"
              min={minSalary}
              max={maxSalary}
            />
            <input
              required
              type="number"
              value={Math.floor(range.to)}
              onChange={(e) => setRange(range.from, Number(e.target.value))}
              placeholder="To"
              className="focus:ring-transparent mt-[10px] rounded-[4px] !outline-none p-[5px] w-full h-[34px] border border-[#B9BDC2] text-[12px] font-normal"
              min={minSalary}
              max={maxSalary}
            />
          </div>

          <div className="flex flex-row items-center justify-end w-full">
            <button
              className="font-normal text-[12px] text-[#4DBFDA]"
              onClick={resetRange}
            >
              {FiltersTranslation["clear"]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryFilter;
